import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Spinner } from './Spinner'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md'

/*
 * Squared corners (3px, not 8–16px) throughout: the board is built from
 * rectangular flaps and plates, and rounded pills fight that. Labels are set in
 * the signage face so controls read as part of the same signed system.
 */
const BASE =
  'signage inline-flex items-center justify-center gap-2 rounded-[3px] transition select-none disabled:cursor-not-allowed disabled:opacity-45'

const VARIANTS: Record<Variant, string> = {
  // Amber plate with dark type — the highest-contrast pairing available here,
  // and the only place the accent is used as a fill.
  primary: 'bg-accent text-canvas hover:bg-accent-strong active:bg-accent-strong',
  secondary: 'border border-line-strong bg-elevated text-ink hover:border-accent/60 hover:bg-line',
  ghost: 'text-muted hover:bg-elevated hover:text-ink',
  danger: 'border border-danger/40 bg-danger-dim text-danger hover:bg-danger/20',
}

/*
 * Both sizes clear a 40px hit target even at `sm`, because this is used on
 * phones far more than on desktop and the 32px buttons it replaced sat below
 * every touch-target guideline.
 */
const SIZES: Record<Size, string> = {
  sm: 'h-10 px-3 text-[10px]',
  md: 'h-11 px-4 text-[11px]',
}

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant
  size?: Size
  loading?: boolean
  children?: ReactNode
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  className = '',
  disabled,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={`${BASE} ${VARIANTS[variant]} ${SIZES[size]} ${className}`}
      {...rest}
    >
      {loading ? <Spinner className="size-4" /> : null}
      {children}
    </button>
  )
}
